import { collection, doc, setDoc, getDoc, getDocs, addDoc, deleteDoc, updateDoc, onSnapshot, query, where, orderBy, limit, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

// ---- HELPERS ----

function companyRef(companyId) { return doc(db, "companies", companyId); }
function subCol(companyId, name) { return collection(db, "companies", companyId, name); }
function subDoc(companyId, name, docId) { return doc(db, "companies", companyId, name, String(docId)); }

function normalizePhoneE164(phone) {
  var digits = (phone || "").replace(/\D/g, "");
  if (digits.length === 10) return "+1" + digits;
  if (digits.length === 11 && digits[0] === "1") return "+" + digits;
  return "+" + digits;
}

// ---- HOSPITALS ----

function listenHospitals(companyId, callback) {
  return onSnapshot(subCol(companyId, "hospitals"), function (snap) {
    var hosps = [];
    snap.forEach(function (d) { hosps.push({ id: +d.id, ...d.data() }); });
    hosps.sort(function (a, b) { return a.id - b.id; });
    // Attach shifts and staffingRules from subcollections
    callback(hosps);
  });
}

function saveHospital(companyId, hospital) {
  var data = {
    name: hospital.name,
    short: hospital.short,
    location: hospital.location || "",
    status: hospital.status || "active",
    credentialTypes: hospital.credentialTypes || []
  };
  // Only touch the recipient list when the caller supplies it, so other saves keep it.
  if (Array.isArray(hospital.scheduleEmails)) data.scheduleEmails = hospital.scheduleEmails;
  return setDoc(subDoc(companyId, "hospitals", hospital.id), data, { merge: true });
}

function deleteHospital(companyId, hospitalId) {
  return deleteDoc(subDoc(companyId, "hospitals", hospitalId));
}

// ---- SHIFTS (subcollection of hospitals) ----

function shiftsCol(companyId, hospitalId) {
  return collection(db, "companies", companyId, "hospitals", String(hospitalId), "shifts");
}
function shiftDoc(companyId, hospitalId, shiftId) {
  return doc(db, "companies", companyId, "hospitals", String(hospitalId), "shifts", String(shiftId));
}

function listenShifts(companyId, hospitalId, callback) {
  return onSnapshot(shiftsCol(companyId, hospitalId), function (snap) {
    var shifts = [];
    snap.forEach(function (d) { shifts.push({ id: +d.id, ...d.data() }); });
    shifts.sort(function (a, b) { return a.id - b.id; });
    callback(shifts);
  });
}

function saveShift(companyId, hospitalId, shift) {
  var data = {
    name: shift.name,
    role: shift.role,
    start: shift.start,
    end: shift.end,
    color: shift.color || "#64748b",
    credentialReqs: shift.credentialReqs || []
  };
  return setDoc(shiftDoc(companyId, hospitalId, shift.id), data, { merge: true });
}

function deleteShift(companyId, hospitalId, shiftId) {
  return deleteDoc(shiftDoc(companyId, hospitalId, shiftId));
}

// ---- STAFFING RULES (subcollection of hospitals) ----

function staffingRulesCol(companyId, hospitalId) {
  return collection(db, "companies", companyId, "hospitals", String(hospitalId), "staffingRules");
}
function staffingRuleDoc(companyId, hospitalId, ruleId) {
  return doc(db, "companies", companyId, "hospitals", String(hospitalId), "staffingRules", String(ruleId));
}

function listenStaffingRules(companyId, hospitalId, callback) {
  return onSnapshot(staffingRulesCol(companyId, hospitalId), function (snap) {
    var rules = [];
    snap.forEach(function (d) { rules.push({ id: d.id, ...d.data() }); });
    callback(rules);
  });
}

function saveStaffingRule(companyId, hospitalId, rule) {
  var id = rule.id || rule.shift;
  return setDoc(staffingRuleDoc(companyId, hospitalId, id), {
    shift: rule.shift,
    minimum: rule.minimum || 0,
    required: rule.required || false
  }, { merge: true });
}

// ---- FULL HOSPITAL LOADER (hospitals + nested shifts + staffingRules) ----

function listenHospitalsFull(companyId, callback) {
  var hosps = [];
  var shiftMap = {};
  var rulesMap = {};
  var unsubs = [];
  var hospUnsub = null;
  var initialized = false;

  function rebuild() {
    var full = hosps.map(function (h) {
      return {
        ...h,
        shifts: shiftMap[h.id] || [],
        staffingRules: rulesMap[h.id] || []
      };
    });
    callback(full);
  }

  hospUnsub = onSnapshot(subCol(companyId, "hospitals"), function (snap) {
    // Clean up old shift/rule listeners
    unsubs.forEach(function (u) { u(); });
    unsubs = [];
    shiftMap = {};
    rulesMap = {};

    hosps = [];
    snap.forEach(function (d) { hosps.push({ id: +d.id, ...d.data() }); });
    hosps.sort(function (a, b) { return a.id - b.id; });

    if (hosps.length === 0) { callback([]); return; }

    var pending = hosps.length * 2;
    function tick() { pending--; if (pending <= 0 || initialized) { initialized = true; rebuild(); } }

    hosps.forEach(function (h) {
      unsubs.push(onSnapshot(shiftsCol(companyId, h.id), function (sSnap) {
        var shifts = [];
        sSnap.forEach(function (d) { shifts.push({ id: +d.id, ...d.data() }); });
        shifts.sort(function (a, b) { return a.id - b.id; });
        shiftMap[h.id] = shifts;
        tick();
      }));
      unsubs.push(onSnapshot(staffingRulesCol(companyId, h.id), function (rSnap) {
        var rules = [];
        rSnap.forEach(function (d) { rules.push({ id: d.id, ...d.data() }); });
        rulesMap[h.id] = rules;
        tick();
      }));
    });
  });

  return function () {
    if (hospUnsub) hospUnsub();
    unsubs.forEach(function (u) { u(); });
  };
}

// ---- PROVIDERS ----

function listenProviders(companyId, callback) {
  return onSnapshot(subCol(companyId, "providers"), function (snap) {
    var provs = [];
    snap.forEach(function (d) { provs.push({ id: +d.id, ...d.data() }); });
    provs.sort(function (a, b) { return a.id - b.id; });
    callback(provs);
  });
}

function saveProvider(companyId, provider) {
  var data = {
    name: provider.name,
    role: provider.role,
    subRole: provider.subRole || "",
    phone: provider.phone || "",
    email: provider.email || "",
    status: provider.status || "active",
    sites: provider.sites || [],
    targetShifts: provider.targetShifts || 0,
    employmentType: provider.employmentType || "full_time",
    maxConsecutive: provider.maxConsecutive || 0,
    qualifications: provider.qualifications || []
  };
  return setDoc(subDoc(companyId, "providers", provider.id), data, { merge: true });
}

function deleteProvider(companyId, providerId) {
  return deleteDoc(subDoc(companyId, "providers", providerId));
}

// ---- CREDENTIALS (subcollection of providers) ----

function credsCol(companyId, providerId) {
  return collection(db, "companies", companyId, "providers", String(providerId), "credentials");
}
function credDoc(companyId, providerId, credId) {
  return doc(db, "companies", companyId, "providers", String(providerId), "credentials", String(credId));
}

function listenCredentials(companyId, providerId, callback) {
  return onSnapshot(credsCol(companyId, providerId), function (snap) {
    var creds = [];
    snap.forEach(function (d) { creds.push({ id: d.id, ...d.data() }); });
    callback(creds);
  });
}

function saveCredential(companyId, providerId, cred) {
  var id = cred.id || (cred.type || "unknown").replace(/\s/g, "_");
  return setDoc(credDoc(companyId, providerId, id), {
    type: cred.type,
    expires: cred.expires,
    licenseNum: cred.licenseNum || "",
    status: cred.status || "current"
  }, { merge: true });
}

function deleteCredential(companyId, providerId, credId) {
  return deleteDoc(credDoc(companyId, providerId, credId));
}

// ---- FULL PROVIDER LOADER (providers + nested credentials) ----

function listenProvidersFull(companyId, callback) {
  var provs = [];
  var credMap = {};
  var unsubs = [];
  var provUnsub = null;
  var initialized = false;

  function rebuild() {
    var full = provs.map(function (p) {
      return { ...p, credentials: credMap[p.id] || [] };
    });
    callback(full);
  }

  provUnsub = onSnapshot(subCol(companyId, "providers"), function (snap) {
    unsubs.forEach(function (u) { u(); });
    unsubs = [];
    credMap = {};

    provs = [];
    snap.forEach(function (d) { provs.push({ id: +d.id, ...d.data() }); });
    provs.sort(function (a, b) { return a.id - b.id; });

    if (provs.length === 0) { callback([]); return; }

    var pending = provs.length;
    function tick() { pending--; if (pending <= 0 || initialized) { initialized = true; rebuild(); } }

    provs.forEach(function (p) {
      unsubs.push(onSnapshot(credsCol(companyId, p.id), function (cSnap) {
        var creds = [];
        cSnap.forEach(function (d) { creds.push({ id: d.id, ...d.data() }); });
        credMap[p.id] = creds;
        tick();
      }));
    });
  });

  return function () {
    if (provUnsub) provUnsub();
    unsubs.forEach(function (u) { u(); });
  };
}

// ---- ASSIGNMENTS ----

function listenAssignments(companyId, monthKey, callback) {
  var q = query(subCol(companyId, "assignments"), where("monthKey", "==", monthKey));
  return onSnapshot(q, function (snap) {
    var asgn = {};
    snap.forEach(function (d) { asgn[d.id] = d.data().providerId; });
    callback(asgn);
  });
}

function listenAllAssignments(companyId, callback) {
  return onSnapshot(subCol(companyId, "assignments"), function (snap) {
    var asgn = {};
    snap.forEach(function (d) { asgn[d.id] = d.data().providerId; });
    callback(asgn);
  });
}

function setAssignment(companyId, key, providerId) {
  var parts = key.split("_");
  if (parts.length !== 3 || !parts[0] || isNaN(+parts[1]) || isNaN(+parts[2])) {
    console.warn("setAssignment: invalid key format:", key);
    return Promise.resolve();
  }
  if (!providerId || isNaN(+providerId)) {
    console.warn("setAssignment: invalid providerId:", providerId);
    return Promise.resolve();
  }
  return setDoc(subDoc(companyId, "assignments", key), {
    date: parts[0],
    hospitalId: +parts[1],
    shiftId: +parts[2],
    providerId: +providerId,
    monthKey: parts[0].slice(0, 7)
  });
}

function deleteAssignment(companyId, key) {
  return deleteDoc(subDoc(companyId, "assignments", key));
}

function batchSetAssignments(companyId, assignments) {
  var batch = writeBatch(db);
  Object.keys(assignments).forEach(function (key) {
    var parts = key.split("_");
    var ref = subDoc(companyId, "assignments", key);
    batch.set(ref, {
      date: parts[0],
      hospitalId: +parts[1],
      shiftId: +parts[2],
      providerId: +assignments[key],
      monthKey: parts[0].slice(0, 7)
    });
  });
  return batch.commit();
}

function batchDeleteAssignments(companyId, monthKey) {
  var q = query(subCol(companyId, "assignments"), where("monthKey", "==", monthKey));
  return getDocs(q).then(function (snap) {
    var batch = writeBatch(db);
    snap.forEach(function (d) { batch.delete(d.ref); });
    return batch.commit();
  });
}

// ---- SCHEDULE CONFIG ----

function listenScheduleConfig(companyId, monthKey, callback) {
  return onSnapshot(subDoc(companyId, "scheduleConfig", monthKey), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  });
}

function listenAllScheduleConfigs(companyId, callback) {
  return onSnapshot(subCol(companyId, "scheduleConfig"), function (snap) {
    var configs = {};
    snap.forEach(function (d) { configs[d.id] = d.data(); });
    callback(configs);
  });
}

function saveScheduleConfig(companyId, monthKey, config) {
  return setDoc(subDoc(companyId, "scheduleConfig", monthKey), config, { merge: true });
}

// ---- TIME-OFFS ----

function listenTimeoffs(companyId, callback) {
  return onSnapshot(subCol(companyId, "timeoffs"), function (snap) {
    var tos = [];
    snap.forEach(function (d) { tos.push({ id: +d.id, ...d.data() }); });
    tos.sort(function (a, b) { return a.id - b.id; });
    callback(tos);
  });
}

function saveTimeoff(companyId, timeoff) {
  return setDoc(subDoc(companyId, "timeoffs", timeoff.id), {
    providerId: timeoff.providerId,
    startDate: timeoff.startDate,
    endDate: timeoff.endDate,
    reason: timeoff.reason || "",
    status: timeoff.status || "pending"
  }, { merge: true });
}

function deleteTimeoff(companyId, timeoffId) {
  return deleteDoc(subDoc(companyId, "timeoffs", timeoffId));
}

// ---- SWAPS ----

function listenSwaps(companyId, callback) {
  return onSnapshot(subCol(companyId, "swaps"), function (snap) {
    var swaps = [];
    snap.forEach(function (d) { swaps.push({ id: +d.id, ...d.data() }); });
    swaps.sort(function (a, b) { return a.id - b.id; });
    callback(swaps);
  });
}

function saveSwap(companyId, swap) {
  return setDoc(subDoc(companyId, "swaps", swap.id), {
    requestorId: swap.requestorId,
    assignmentKey: swap.assignmentKey,
    type: swap.type,
    targetId: swap.targetId || null,
    targetIds: swap.targetIds || [],
    targetAssignmentKey: swap.targetAssignmentKey || null,
    acceptedBy: swap.acceptedBy || null,
    declinedBy: swap.declinedBy || [],
    message: swap.message || "",
    reason: swap.reason || "",
    status: swap.status || "pending",
    createdAt: swap.createdAt || new Date().toISOString().slice(0, 10)
  }, { merge: true });
}

// ---- AVAILABILITY ----

function listenAvailability(companyId, callback) {
  return onSnapshot(subCol(companyId, "availability"), function (snap) {
    var avails = [];
    snap.forEach(function (d) { avails.push({ id: +d.id, ...d.data() }); });
    callback(avails);
  });
}

function saveAvailability(companyId, avail) {
  // Full overwrite on purpose. With { merge: true } the unavailableDays map was
  // merged key by key, so a day a provider un-marked in the portal was never
  // removed server-side; days could only ever be added. Every caller passes the
  // complete record, so replacing the document is correct.
  return setDoc(subDoc(companyId, "availability", avail.id), {
    providerId: avail.providerId,
    year: avail.year,
    month: avail.month,
    unavailableDays: avail.unavailableDays || {},
    notes: avail.notes || "",
    submittedAt: avail.submittedAt || new Date().toISOString().slice(0, 10)
  });
}

// ---- SCHEDULING RULES ----

function listenSchedRules(companyId, callback) {
  return onSnapshot(subCol(companyId, "schedRules"), function (snap) {
    var rules = [];
    snap.forEach(function (d) { rules.push({ id: +d.id, ...d.data() }); });
    callback(rules);
  });
}

function saveSchedRule(companyId, rule) {
  return setDoc(subDoc(companyId, "schedRules", rule.id), {
    providerId: rule.providerId,
    type: rule.type,
    level: rule.level || null,
    hospitalId: rule.hospitalId || null,
    shiftId: rule.shiftId || null,
    shiftName: rule.shiftName || null,
    days: rule.days || null,
    value: rule.value || null,
    note: rule.note || "",
    monthKey: rule.monthKey || null
  }, { merge: true });
}

function deleteSchedRule(companyId, ruleId) {
  return deleteDoc(subDoc(companyId, "schedRules", ruleId));
}

// ---- PAY RATES ----

function listenPayRates(companyId, callback) {
  return onSnapshot(subCol(companyId, "payRates"), function (snap) {
    var rates = [];
    snap.forEach(function (d) { rates.push({ id: +d.id, ...d.data() }); });
    callback(rates);
  });
}

function savePayRate(companyId, rate) {
  return setDoc(subDoc(companyId, "payRates", rate.id), {
    providerId: rate.providerId || null,
    hospitalId: rate.hospitalId || null,
    shiftId: rate.shiftId || null,
    role: rate.role || null,
    hourlyRate: rate.hourlyRate,
    label: rate.label || ""
  }, { merge: true });
}

function deletePayRate(companyId, rateId) {
  return deleteDoc(subDoc(companyId, "payRates", rateId));
}

// ---- SHIFT BONUSES ----

function listenShiftBonuses(companyId, callback) {
  return onSnapshot(subCol(companyId, "shiftBonuses"), function (snap) {
    var bonuses = {};
    snap.forEach(function (d) { bonuses[d.id] = d.data(); });
    callback(bonuses);
  });
}

function saveShiftBonus(companyId, assignmentKey, data) {
  return setDoc(subDoc(companyId, "shiftBonuses", assignmentKey), {
    amount: data.amount,
    reason: data.reason || ""
  });
}

function deleteShiftBonus(companyId, assignmentKey) {
  return deleteDoc(subDoc(companyId, "shiftBonuses", assignmentKey));
}

// ---- AD-HOC BONUSES ----

function listenBonuses(companyId, callback) {
  return onSnapshot(subCol(companyId, "bonuses"), function (snap) {
    var bonuses = [];
    snap.forEach(function (d) { bonuses.push({ id: +d.id, ...d.data() }); });
    callback(bonuses);
  });
}

function saveBonus(companyId, bonus) {
  return setDoc(subDoc(companyId, "bonuses", bonus.id), {
    providerId: bonus.providerId,
    monthKey: bonus.monthKey,
    amount: bonus.amount,
    reason: bonus.reason || "",
    date: bonus.date || ""
  });
}

function deleteBonus(companyId, bonusId) {
  return deleteDoc(subDoc(companyId, "bonuses", bonusId));
}

// ---- TIMECARDS ----

function listenTimecards(companyId, callback) {
  return onSnapshot(subCol(companyId, "timecards"), function (snap) {
    var tcs = [];
    snap.forEach(function (d) { tcs.push({ id: +d.id, ...d.data() }); });
    callback(tcs);
  });
}

function saveTimecard(companyId, tc) {
  return setDoc(subDoc(companyId, "timecards", tc.id), {
    providerId: tc.providerId,
    monthKey: tc.monthKey,
    status: tc.status || "draft",
    signedAt: tc.signedAt || null,
    approvedAt: tc.approvedAt || null,
    approvedBy: tc.approvedBy || null,
    disputeNote: tc.disputeNote || null
  }, { merge: true });
}

// ---- TIMECARD ENTRIES ----

function listenTimecardEntries(companyId, callback) {
  return onSnapshot(subCol(companyId, "timecardEntries"), function (snap) {
    var entries = {};
    snap.forEach(function (d) { entries[d.id] = d.data(); });
    callback(entries);
  });
}

function saveTimecardEntry(companyId, assignmentKey, entry) {
  return setDoc(subDoc(companyId, "timecardEntries", assignmentKey), {
    timeIn: entry.timeIn || null,
    timeOut: entry.timeOut || null
  }, { merge: true });
}

// ---- SHIFT OVERRIDES ----

function listenShiftOverrides(companyId, callback) {
  return onSnapshot(subCol(companyId, "shiftOverrides"), function (snap) {
    var overrides = {};
    snap.forEach(function (d) { overrides[d.id] = d.data(); });
    callback(overrides);
  });
}

function saveShiftOverride(companyId, assignmentKey, data) {
  return setDoc(subDoc(companyId, "shiftOverrides", assignmentKey), {
    start: data.start || null,
    end: data.end || null
  }, { merge: true });
}

function deleteShiftOverride(companyId, assignmentKey) {
  return deleteDoc(subDoc(companyId, "shiftOverrides", assignmentKey));
}

// ---- CUSTOM SHIFTS ----

function listenCustomShifts(companyId, callback) {
  return onSnapshot(subCol(companyId, "customShifts"), function (snap) {
    var shifts = [];
    snap.forEach(function (d) { shifts.push({ id: d.id, ...d.data() }); });
    callback(shifts);
  });
}

function saveCustomShift(companyId, cs) {
  var id = cs.id || (String(Date.now()) + String(Math.floor(Math.random() * 10000)));
  return setDoc(subDoc(companyId, "customShifts", id), {
    date: cs.date,
    hospId: cs.hospId,
    name: cs.name || "Extra",
    start: cs.start || "06:00",
    end: cs.end || "18:00",
    role: cs.role || "MD/Midlevel",
    providerId: cs.providerId || null
  });
}

function deleteCustomShift(companyId, id) {
  return deleteDoc(subDoc(companyId, "customShifts", id));
}

// ---- AUDIT LOG ----

function addAuditEntry(companyId, entry) {
  var id = String(Date.now()) + String(Math.floor(Math.random() * 10000));
  return setDoc(subDoc(companyId, "auditLog", id), {
    timestamp: entry.timestamp || new Date().toISOString(),
    actor: entry.actor || "Admin",
    action: entry.action,
    description: entry.description,
    details: entry.details || null
  });
}

function listenAuditLog(companyId, callback, maxEntries) {
  var q = query(subCol(companyId, "auditLog"), orderBy("timestamp", "desc"), limit(maxEntries || 1000));
  return onSnapshot(q, function (snap) {
    var entries = [];
    snap.forEach(function (d) { entries.push({ id: d.id, ...d.data() }); });
    callback(entries);
  });
}

// ---- NOTIFICATIONS ----

function listenNotifications(companyId, callback) {
  var q = query(subCol(companyId, "notifications"), orderBy("createdAt", "desc"), limit(200));
  return onSnapshot(q, function (snap) {
    var notifs = [];
    snap.forEach(function (d) { notifs.push({ id: d.id, ...d.data() }); });
    callback(notifs);
  });
}

function addNotificationDoc(companyId, notif) {
  var id = String(Date.now()) + String(Math.floor(Math.random() * 10000));
  return setDoc(subDoc(companyId, "notifications", id), {
    recipientId: notif.recipientId,
    type: notif.type,
    title: notif.title,
    message: notif.message,
    read: false,
    createdAt: notif.createdAt || new Date().toISOString(),
    relatedSwapId: notif.relatedSwapId || null
  });
}

function markNotificationRead(companyId, notifId) {
  return updateDoc(subDoc(companyId, "notifications", notifId), { read: true });
}

function markAllNotificationsRead(companyId, recipientId) {
  var q = query(subCol(companyId, "notifications"), where("recipientId", "==", recipientId), where("read", "==", false));
  return getDocs(q).then(function (snap) {
    var batch = writeBatch(db);
    snap.forEach(function (d) { batch.update(d.ref, { read: true }); });
    return batch.commit();
  });
}

// ---- NOTIFICATION QUEUE (for outbound SMS/email, Phase 2) ----

function queueNotification(companyId, notification) {
  if (!companyId || !notification || !notification.recipientId || !notification.type) {
    console.warn("queueNotification: missing required fields, skipping");
    return Promise.resolve();
  }
  var channel = notification.channel || "both";
  if (["sms", "email", "both"].indexOf(channel) < 0) channel = "both";
  var body = (notification.body || "").slice(0, 2000);
  var subject = (notification.subject || "").slice(0, 200);
  return addDoc(subCol(companyId, "notificationQueue"), {
    recipientId: String(notification.recipientId),
    recipientPhone: notification.recipientPhone || "",
    recipientEmail: notification.recipientEmail || "",
    channel: channel,
    type: notification.type,
    subject: subject,
    body: body,
    relatedId: notification.relatedId || null,
    companyId: companyId,
    status: "pending",
    error: null,
    createdAt: serverTimestamp(),
    sentAt: null,
    retryCount: 0
  });
}

// ---- MESSAGES (chatbot conversation history, Phase 3) ----

function listenMessages(companyId, providerId, callback) {
  var q = query(subCol(companyId, "messages"), where("providerId", "==", providerId), orderBy("createdAt", "desc"), limit(50));
  return onSnapshot(q, function (snap) {
    var msgs = [];
    snap.forEach(function (d) { msgs.push({ id: d.id, ...d.data() }); });
    callback(msgs.reverse());
  });
}

function listenAllMessages(companyId, callback) {
  var q = query(subCol(companyId, "messages"), orderBy("createdAt", "desc"), limit(200));
  return onSnapshot(q, function (snap) {
    var msgs = [];
    snap.forEach(function (d) { msgs.push({ id: d.id, ...d.data() }); });
    callback(msgs);
  });
}

// ---- PROVIDER PREFERENCES (Phase 2) ----

function listenProviderPreferences(companyId, providerId, callback) {
  return onSnapshot(subDoc(companyId, "providerPreferences", providerId), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  });
}

// Exact consent sentence shown beside the portal's SMS toggle. It is stored on the
// preferences doc at opt-in and must match the wording filed with Twilio for
// toll-free verification (exports/twilio-toll-free-verification-answers.md).
var SMS_CONSENT_TEXT = "By checking this box, I agree to receive SMS text messages from RosterDr at the mobile number above about my work schedule: requests for my availability, notices when a schedule is published, reminders before my shifts, and confirmations of my swap and time-off requests. Consent is optional and is not a condition of employment or of using RosterDr. Message frequency varies. Message and data rates may apply. Reply STOP to opt out at any time or HELP for help.";

// Saves preferences and records when SMS consent was given or withdrawn, so
// there is a timestamped opt-in record for carrier (A2P 10DLC / toll-free) compliance.
// smsConsentAt / smsOptOutAt are server timestamps (not the device clock) and the
// consent sentence the provider saw is stored alongside them.
async function saveProviderPreferences(companyId, providerId, prefs) {
  var ref = subDoc(companyId, "providerPreferences", providerId);
  var update = { ...prefs };
  // The phone the provider saw when consenting is only recorded at opt-in time.
  var consentPhone = update.smsConsentPhone || null;
  delete update.smsConsentPhone;
  if (prefs.smsEnabled === true || prefs.smsEnabled === false) {
    var snap = await getDoc(ref);
    var current = snap.exists() ? snap.data() : {};
    if (prefs.smsEnabled && current.smsEnabled !== true) {
      update.smsConsentAt = serverTimestamp();
      update.smsConsentSource = "portal";
      update.smsConsentText = SMS_CONSENT_TEXT;
      update.smsConsentPhone = consentPhone;
    }
    if (!prefs.smsEnabled && current.smsEnabled !== false) {
      update.smsOptOutAt = serverTimestamp();
      update.smsOptOutSource = "portal";
    }
  }
  return setDoc(ref, update, { merge: true });
}

// Admin view: all providers' preferences keyed by providerId
function listenAllProviderPreferences(companyId, callback) {
  return onSnapshot(subCol(companyId, "providerPreferences"), function (snap) {
    var byId = {};
    snap.forEach(function (d) { byId[d.id] = d.data(); });
    callback(byId);
  });
}

// ---- PROVIDER SCHEDULE PREFERENCES (standing preferences with admin review) ----

function listenProviderSchedulePrefs(companyId, callback) {
  return onSnapshot(subCol(companyId, "providerSchedulePrefs"), function (snap) {
    var prefs = [];
    snap.forEach(function (d) { prefs.push({ id: d.id, ...d.data() }); });
    callback(prefs);
  });
}

function saveProviderSchedulePref(companyId, pref) {
  var id = pref.id || String(Date.now()) + String(Math.floor(Math.random() * 10000));
  return setDoc(subDoc(companyId, "providerSchedulePrefs", id), {
    providerId: pref.providerId,
    text: pref.text || "",
    status: pref.status || "pending",
    submittedAt: pref.submittedAt || new Date().toISOString(),
    reviewedAt: pref.reviewedAt || null,
    convertedRuleId: pref.convertedRuleId || null
  }, { merge: true });
}

function deleteProviderSchedulePref(companyId, prefId) {
  return deleteDoc(subDoc(companyId, "providerSchedulePrefs", prefId));
}

// ---- SMS CONVERSATIONS (Phase 3 — conversational AI via SMS) ----

function listenSmsConversations(companyId, callback) {
  return onSnapshot(subCol(companyId, "smsConversations"), function (snap) {
    var convos = [];
    snap.forEach(function (d) { convos.push({ id: d.id, ...d.data() }); });
    convos.sort(function (a, b) {
      var aTime = a.lastMessageAt && a.lastMessageAt.toDate ? a.lastMessageAt.toDate().getTime() : 0;
      var bTime = b.lastMessageAt && b.lastMessageAt.toDate ? b.lastMessageAt.toDate().getTime() : 0;
      return bTime - aTime;
    });
    callback(convos);
  });
}

function saveSmsConversation(companyId, providerId, data) {
  return setDoc(subDoc(companyId, "smsConversations", String(providerId)), data, { merge: true });
}

function listenSmsMessages(companyId, providerId, callback) {
  var col = collection(db, "companies", companyId, "smsConversations", String(providerId), "messages");
  var q = query(col, orderBy("timestamp", "asc"));
  return onSnapshot(q, function (snap) {
    var msgs = [];
    snap.forEach(function (d) { msgs.push({ id: d.id, ...d.data() }); });
    callback(msgs);
  });
}

// ---- PROVIDER METRICS (Phase 4 — RVUs, patients, acuity) ----

function listenProviderMetrics(companyId, callback) {
  return onSnapshot(subCol(companyId, "providerMetrics"), function (snap) {
    var metrics = [];
    snap.forEach(function (d) { metrics.push({ id: d.id, ...d.data() }); });
    callback(metrics);
  });
}

function saveProviderMetric(companyId, metric) {
  var id = metric.id || (metric.providerId + "_" + metric.monthKey);
  return setDoc(subDoc(companyId, "providerMetrics", id), {
    providerId: metric.providerId,
    monthKey: metric.monthKey,
    rvus: metric.rvus || 0,
    patients: metric.patients || 0,
    avgAcuity: metric.avgAcuity || 0,
    source: metric.source || "csv_import",
    importedAt: metric.importedAt || new Date().toISOString()
  }, { merge: true });
}

// ---- DATA SEEDING ----

function seedInitialData(companyId, initData) {
  var batch = writeBatch(db);

  // Hospitals + shifts + staffingRules
  (initData.hospitals || []).forEach(function (h) {
    batch.set(subDoc(companyId, "hospitals", h.id), {
      name: h.name, short: h.short, location: h.location || "",
      status: h.status || "active", credentialTypes: h.credentialTypes || []
    });
    (h.shifts || []).forEach(function (s) {
      batch.set(shiftDoc(companyId, h.id, s.id), {
        name: s.name, role: s.role, start: s.start, end: s.end,
        color: s.color || "#64748b", credentialReqs: s.credentialReqs || []
      });
    });
    (h.staffingRules || []).forEach(function (r) {
      batch.set(staffingRuleDoc(companyId, h.id, r.shift), {
        shift: r.shift, minimum: r.minimum || 0, required: r.required || false
      });
    });
  });

  // Providers + credentials
  (initData.providers || []).forEach(function (p) {
    batch.set(subDoc(companyId, "providers", p.id), {
      name: p.name, role: p.role, phone: p.phone || "", email: p.email || "",
      status: p.status || "active", sites: p.sites || [],
      targetShifts: p.targetShifts || 0, employmentType: p.employmentType || "full_time",
      maxConsecutive: p.maxConsecutive || 0, qualifications: p.qualifications || []
    });
    (p.credentials || []).forEach(function (c) {
      var cId = (c.siteId + "_" + c.type).replace(/\s/g, "_");
      batch.set(credDoc(companyId, p.id, cId), {
        siteId: c.siteId, site: c.site || "", type: c.type,
        expires: c.expires, licenseNum: c.licenseNum || "", status: c.status || "current"
      });
    });
  });

  // Time-offs
  (initData.timeoffs || []).forEach(function (t) {
    batch.set(subDoc(companyId, "timeoffs", t.id), {
      providerId: t.providerId, startDate: t.startDate, endDate: t.endDate,
      reason: t.reason || "", status: t.status || "pending"
    });
  });

  // Swaps
  (initData.swaps || []).forEach(function (s) {
    batch.set(subDoc(companyId, "swaps", s.id), {
      requestorId: s.requestorId, assignmentKey: s.assignmentKey, type: s.type,
      targetId: s.targetId || null, targetIds: s.targetIds || [],
      targetAssignmentKey: s.targetAssignmentKey || null,
      acceptedBy: s.acceptedBy || null, declinedBy: s.declinedBy || [],
      message: s.message || "", reason: s.reason || "",
      status: s.status || "pending", createdAt: s.createdAt || ""
    });
  });

  // Availability
  (initData.availability || []).forEach(function (a) {
    batch.set(subDoc(companyId, "availability", a.id), {
      providerId: a.providerId, year: a.year, month: a.month,
      unavailableDays: a.unavailableDays || {}, notes: a.notes || "",
      submittedAt: a.submittedAt || ""
    });
  });

  // Scheduling rules
  (initData.schedRules || []).forEach(function (r) {
    batch.set(subDoc(companyId, "schedRules", r.id), {
      providerId: r.providerId, type: r.type, level: r.level || null,
      hospitalId: r.hospitalId || null, shiftId: r.shiftId || null,
      shiftName: r.shiftName || null, days: r.days || null, note: r.note || ""
    });
  });

  // Pay rates
  (initData.payRates || []).forEach(function (r) {
    batch.set(subDoc(companyId, "payRates", r.id), {
      providerId: r.providerId || null, hospitalId: r.hospitalId || null,
      shiftId: r.shiftId || null, role: r.role || null,
      hourlyRate: r.hourlyRate, label: r.label || ""
    });
  });

  return batch.commit();
}

function checkNeedsSeed(companyId) {
  return getDocs(query(subCol(companyId, "hospitals"), limit(1))).then(function (snap) {
    return snap.empty;
  });
}

// ---- CLEAR ALL COMPANY DATA ----

async function deleteCollectionDocs(colRef) {
  var snap = await getDocs(colRef);
  var deletes = [];
  snap.forEach(function (d) { deletes.push(deleteDoc(d.ref)); });
  if (deletes.length > 0) await Promise.all(deletes);
  return deletes.length;
}

async function clearAllCompanyData(companyId) {
  // 1. Delete nested subcollections under hospitals (shifts, staffingRules)
  var hospSnap = await getDocs(subCol(companyId, "hospitals"));
  for (var i = 0; i < hospSnap.docs.length; i++) {
    var hDoc = hospSnap.docs[i];
    await deleteCollectionDocs(collection(db, "companies", companyId, "hospitals", hDoc.id, "shifts"));
    await deleteCollectionDocs(collection(db, "companies", companyId, "hospitals", hDoc.id, "staffingRules"));
  }
  // 2. Delete nested subcollections under providers (credentials)
  var provSnap = await getDocs(subCol(companyId, "providers"));
  for (var j = 0; j < provSnap.docs.length; j++) {
    var pDoc = provSnap.docs[j];
    await deleteCollectionDocs(collection(db, "companies", companyId, "providers", pDoc.id, "credentials"));
  }
  // 3. Delete all top-level subcollections one by one
  var cols = ["hospitals", "providers", "assignments", "scheduleConfig", "timeoffs", "swaps", "availability", "schedRules", "payRates", "shiftBonuses", "bonuses", "timecards", "timecardEntries", "auditLog", "notifications", "notificationQueue", "userPrefs", "providerSchedulePrefs"];
  for (var k = 0; k < cols.length; k++) {
    await deleteCollectionDocs(subCol(companyId, cols[k]));
  }
  console.log("clearAllCompanyData: done for " + companyId);
}

// ---- GENERAL SETTINGS (qualifications list, etc.) ----

function listenGeneralSettings(companyId, callback) {
  return onSnapshot(subDoc(companyId, "settings", "general"), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  });
}

function saveGeneralSettings(companyId, settings) {
  return setDoc(subDoc(companyId, "settings", "general"), settings, { merge: true });
}

// ---- NOTIFICATION SETTINGS (admin-configured SMS/Email credentials) ----

function listenNotificationSettings(companyId, callback) {
  return onSnapshot(subDoc(companyId, "settings", "notifications"), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  });
}

function saveNotificationSettings(companyId, settings) {
  return setDoc(subDoc(companyId, "settings", "notifications"), {
    smsEnabled: settings.smsEnabled || false,
    emailEnabled: settings.emailEnabled || false,
    twilioAccountSid: settings.twilioAccountSid || "",
    twilioAuthToken: settings.twilioAuthToken || "",
    twilioPhoneNumber: settings.twilioPhoneNumber || "",
    emailFromAddress: settings.emailFromAddress || "",
    appUrl: (settings.appUrl || "").trim(),
    adminAlerts: settings.adminAlerts || null,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// ---- SMS OUTREACH SETTINGS ----

function listenSmsOutreachSettings(companyId, callback) {
  return onSnapshot(subDoc(companyId, "settings", "smsOutreach"), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  });
}

function saveSmsOutreachSettings(companyId, settings) {
  return setDoc(subDoc(companyId, "settings", "smsOutreach"), {
    ...settings,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// ---- ASSISTANT CHATS ----
// One document per conversation: companies/{c}/assistantChats/{chatId} { messages: [...] }
function listenAssistantChat(companyId, chatId, callback) {
  return onSnapshot(subDoc(companyId, "assistantChats", chatId), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  }, function () { callback(null); });
}
function saveAssistantChat(companyId, chatId, messages) {
  return setDoc(subDoc(companyId, "assistantChats", chatId), { messages: messages, updatedAt: serverTimestamp() }, { merge: true });
}

// ---- FCM PUSH TOKENS ----

function saveFcmToken(companyId, providerId, token) {
  return setDoc(subDoc(companyId, "fcmTokens", String(providerId)), {
    token: token,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

function deleteFcmToken(companyId, providerId) {
  return deleteDoc(subDoc(companyId, "fcmTokens", String(providerId)));
}

// ---- GRID PREFERENCES (user-specific, stored per company) ----

function saveGridPreferences(companyId, prefs) {
  return setDoc(subDoc(companyId, "userPrefs", "grid"), {
    gridColumnOrder: prefs.gridColumnOrder || null,
    gridShiftOrder: prefs.gridShiftOrder || null
  }, { merge: true });
}

function listenGridPreferences(companyId, callback) {
  return onSnapshot(subDoc(companyId, "userPrefs", "grid"), function (snap) {
    callback(snap.exists() ? snap.data() : { gridColumnOrder: null, gridShiftOrder: null });
  });
}

export {
  // Helpers
  normalizePhoneE164,
  // Hospitals (full loader with shifts + staffingRules)
  listenHospitalsFull,
  listenHospitals,
  saveHospital,
  deleteHospital,
  // Shifts
  listenShifts,
  saveShift,
  deleteShift,
  // Staffing Rules
  listenStaffingRules,
  saveStaffingRule,
  // Providers (full loader with credentials)
  listenProvidersFull,
  listenProviders,
  saveProvider,
  deleteProvider,
  // Credentials
  listenCredentials,
  saveCredential,
  deleteCredential,
  // Assignments
  listenAssignments,
  listenAllAssignments,
  setAssignment,
  deleteAssignment,
  batchSetAssignments,
  batchDeleteAssignments,
  // Schedule Config
  listenScheduleConfig,
  listenAllScheduleConfigs,
  saveScheduleConfig,
  // Time-offs
  listenTimeoffs,
  saveTimeoff,
  deleteTimeoff,
  // Swaps
  listenSwaps,
  saveSwap,
  // Availability
  listenAvailability,
  saveAvailability,
  // Scheduling Rules
  listenSchedRules,
  saveSchedRule,
  deleteSchedRule,
  // Pay Rates
  listenPayRates,
  savePayRate,
  deletePayRate,
  // Shift Bonuses
  listenShiftBonuses,
  saveShiftBonus,
  deleteShiftBonus,
  // Bonuses
  listenBonuses,
  saveBonus,
  deleteBonus,
  // Timecards
  listenTimecards,
  saveTimecard,
  // Timecard Entries
  listenTimecardEntries,
  saveTimecardEntry,
  // Audit Log
  addAuditEntry,
  listenAuditLog,
  // Notifications
  listenNotifications,
  addNotificationDoc,
  markNotificationRead,
  markAllNotificationsRead,
  // Notification Queue (Phase 2)
  queueNotification,
  // Messages (Phase 3)
  listenMessages,
  listenAllMessages,
  // Provider Preferences (Phase 2)
  listenProviderPreferences,
  SMS_CONSENT_TEXT,
  saveProviderPreferences,
  listenAllProviderPreferences,
  listenAssistantChat,
  saveAssistantChat,
  // Provider Schedule Preferences (standing preferences with admin review)
  listenProviderSchedulePrefs,
  saveProviderSchedulePref,
  deleteProviderSchedulePref,
  // SMS Conversations (Phase 3)
  listenSmsConversations,
  saveSmsConversation,
  listenSmsMessages,
  // Provider Metrics (Phase 4)
  listenProviderMetrics,
  saveProviderMetric,
  // Seeding
  seedInitialData,
  checkNeedsSeed,
  // Notification Settings
  listenNotificationSettings,
  saveNotificationSettings,
  // General Settings
  listenGeneralSettings,
  saveGeneralSettings,
  // Clear All
  clearAllCompanyData,
  // Grid Preferences
  saveGridPreferences,
  listenGridPreferences,
  // Shift Overrides
  listenShiftOverrides,
  saveShiftOverride,
  deleteShiftOverride,
  // Custom Shifts
  listenCustomShifts,
  saveCustomShift,
  deleteCustomShift,
  // SMS Outreach Settings
  listenSmsOutreachSettings,
  saveSmsOutreachSettings,
  // FCM Push Tokens
  saveFcmToken,
  deleteFcmToken
};
